/**
 * McpInfoPage
 *
 * A read-only informational page at /mcp that:
 *  - Explains what the MCP backend provides in this Backstage instance.
 *  - Shows the available MCP server endpoint and the catalog actions it exposes.
 *  - Provides ready-to-paste connection snippets for popular AI tools
 *    (Claude Desktop, Cursor, VS Code Copilot Chat).
 *
 * This page does NOT call the MCP endpoint itself; it is purely informational.
 * MCP clients are external tools that connect directly to the backend URL.
 *
 * Endpoint (local development):
 *   http://localhost:7007/api/mcp-actions/v1/catalog
 *
 * Endpoint (production – Wesecon Backstage):
 *   https://backstage.cloud.wesecon.digital/api/mcp-actions/v1/catalog
 */

import {
  Box,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Content, Header, InfoCard, Page } from '@backstage/core-components';
import CodeIcon from '@material-ui/icons/Code';
import LinkIcon from '@material-ui/icons/Link';
import SecurityIcon from '@material-ui/icons/Security';
import ListIcon from '@material-ui/icons/List';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  codeBlock: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    overflowX: 'auto',
    whiteSpace: 'pre',
    border: `1px solid ${theme.palette.divider}`,
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  endpointRow: {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
  },
  label: {
    color: theme.palette.text.secondary,
    // theme.typography.fontWeightMedium is typed as Property.FontWeight | undefined
    // (csstype), which is wider than the FontWeightProperty JSS accepts here.
    // Casting to number is safe: Material-UI's default fontWeightMedium is 500.
    fontWeight: theme.typography.fontWeightMedium as number,
    marginBottom: theme.spacing(0.5),
    display: 'block' as const,
  },
}));

// ---------------------------------------------------------------------------
// Static data — kept in sync with app-config.yaml mcpActions.servers
// ---------------------------------------------------------------------------

// Local development base URL.
// In production the URL is: https://backstage.cloud.wesecon.digital
// Update the snippets below if you copy them for production use.
const BASE_URL = 'http://localhost:7007';

interface McpServer {
  key: string;
  name: string;
  description: string;
  endpoint: string;
  actions: Array<{ id: string; title: string; description: string; readOnly: boolean }>;
}

// Only the catalog server is registered — scaffolder actions are intentionally excluded.
// See app-config.yaml → mcpActions.servers for the authoritative server list.
const MCP_SERVERS: McpServer[] = [
  {
    key: 'catalog',
    name: 'Backstage Catalog Actions',
    description:
      'Read-only actions for exploring and validating Backstage software catalog entities. ' +
      'All five actions are safe to call from AI clients — none of them modify catalog state.',
    endpoint: `${BASE_URL}/api/mcp-actions/v1/catalog`,
    actions: [
      {
        id: 'fetch-entity',
        title: 'Fetch Catalog Entity',
        description: 'Returns the full metadata of one entity by its ref (kind:namespace/name).',
        readOnly: true,
      },
      {
        id: 'list-entities-by-kind',
        title: 'List Entities by Kind',
        description: 'Lists catalog entities filtered by kind (API, Component, System, …).',
        readOnly: true,
      },
      {
        id: 'summarize-entity-context',
        title: 'Summarize Entity Context',
        description:
          'Returns a structured plain-text block describing an entity — ideal for injecting ' +
          'into an AI system prompt before asking for doc generation or runbook creation.',
        readOnly: true,
      },
      {
        id: 'search-entities',
        title: 'Search Catalog Entities',
        description:
          'Full-text + field search across the entire catalog. ' +
          'Supports free-text query, kind filter, and owner filter in a single call. ' +
          'Set includeFullEntity=true to include the complete Backstage entity object ' +
          '(annotations, links, full spec, etc.) in every result item.',
        readOnly: true,
      },
      {
        id: 'validate-entity',
        title: 'Validate Catalog Entity (dry-run)',
        description:
          'Validates an entity JSON object against the catalog schema without saving it. ' +
          'Use to verify AI-generated catalog-info.yaml before committing to Git.',
        readOnly: true,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Monospace code block with an optional label above it */
function CodeBlock({ label, code }: { label?: string; code: string }) {
  const classes = useStyles();
  return (
    <Box mb={2}>
      {label && (
        <Typography variant="caption" className={classes.label}>
          {label}
        </Typography>
      )}
      <Paper elevation={0} className={classes.codeBlock}>
        {code}
      </Paper>
    </Box>
  );
}

/** Card displaying one MCP server — endpoint URL + actions table */
function ServerCard({ server }: { server: McpServer }) {
  const classes = useStyles();
  return (
    <Box className={classes.section}>
      <InfoCard
        title={server.name}
        subheader={server.description}
        variant="gridItem"
      >
        {/* Endpoint URL */}
        <Box display="flex" alignItems="center" mb={2}>
          <LinkIcon fontSize="small" style={{ marginRight: 6, opacity: 0.6 }}/>
          <Typography variant="body2" component="span" className={classes.endpointRow}>
            {server.endpoint}
          </Typography>
        </Box>

        <Divider/>

        {/* Actions table */}
        <Box mt={2}>
          <Box display="flex" alignItems="center" mb={1}>
            <ListIcon fontSize="small" style={{ marginRight: 6, opacity: 0.6 }}/>
            <Typography variant="subtitle2">Available tools</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '30%' }}>Tool ID</TableCell>
                <TableCell>Description</TableCell>
                <TableCell style={{ width: '80px' }}>Read-only</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {server.actions.map(a => (
                <TableRow key={a.id}>
                  <TableCell>
                    <code style={{ fontSize: '0.78rem' }}>{a.id}</code>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{a.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={a.readOnly ? 'yes' : 'no'}
                      color={a.readOnly ? 'primary' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </InfoCard>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function McpInfoPage() {
  const classes = useStyles();

  return (
    <Page themeId="tool">
      <Header
        title="MCP — Model Context Protocol"
        subtitle="AI integration endpoint exposed by this Backstage backend"
      />
      <Content>

        {/* ── What is MCP? ─────────────────────────────────────────────── */}
        <Box className={classes.section}>
          <InfoCard title="What is MCP?" noPadding={false}>
            <Typography variant="body2" paragraph>
              <strong>Model Context Protocol (MCP)</strong> is an open standard (by Anthropic) that
              allows AI clients — such as Claude Desktop, Cursor, VS Code Copilot Chat, or custom
              agents — to discover and call backend <em>tools</em> in a structured, type-safe way.
            </Typography>
            <Typography variant="body2" paragraph>
              Backstage's <code>@backstage/plugin-mcp-actions-backend</code> exposes registered
              Backstage actions as MCP tools over an HTTP/SSE endpoint. An AI client connects once,
              receives the tool list, and can then invoke any tool with typed inputs to retrieve
              data — all without embedding Backstage API knowledge in the client prompt.
            </Typography>
            <Typography variant="body2">
              This Backstage instance exposes one MCP endpoint for catalog read-only actions
              (see below). All endpoints are authenticated with a static bearer token; configure{' '}
              <code>MCP_STATIC_TOKEN</code> in your environment and supply it as an{' '}
              <code>Authorization: Bearer</code> header in every MCP client connection.
            </Typography>
          </InfoCard>
        </Box>

        {/* ── Authentication ────────────────────────────────────────────── */}
        <Box className={classes.section}>
          <InfoCard
            title="Authentication"
            subheader="All MCP endpoints require a bearer token"
          >
            <Box display="flex" alignItems="flex-start" mb={1}>
              <SecurityIcon fontSize="small" style={{ marginRight: 6, marginTop: 2, opacity: 0.6 }}/>
              <Typography variant="body2">
                Set the <code>MCP_STATIC_TOKEN</code> environment variable on the backend to a
                long random secret (<code>openssl rand -hex 32</code>). Supply it as a{' '}
                <code>Authorization: Bearer &lt;token&gt;</code> header in every MCP client
                connection.
              </Typography>
            </Box>
            <CodeBlock
              label="Verify connectivity — MCP initialize handshake (curl)"
              code={`curl -s -X POST \\
     -H "Authorization: Bearer $MCP_STATIC_TOKEN" \\
     -H "Content-Type: application/json" \\
     -H "Accept: application/json, text/event-stream" \\
     -d '{"jsonrpc":"2.0","id":1,"method":"initialize","params":{"protocolVersion":"2024-11-05","capabilities":{},"clientInfo":{"name":"curl-test","version":"1.0"}}}' \\
     ${BASE_URL}/api/mcp-actions/v1/catalog`}
            />
          </InfoCard>
        </Box>

        {/* ── MCP Servers ───────────────────────────────────────────────── */}
        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            MCP Servers
          </Typography>
          {MCP_SERVERS.map(s => (
            <ServerCard key={s.key} server={s}/>
          ))}
        </Box>

        {/* ── Client configuration snippets ─────────────────────────────── */}
        <Box className={classes.section}>
          <InfoCard
            title="Connect an AI client"
            subheader="Ready-to-paste configuration for popular tools"
          >
            <Box display="flex" alignItems="center" mb={2}>
              <CodeIcon fontSize="small" style={{ marginRight: 6, opacity: 0.6 }}/>
              <Typography variant="body2" color="textSecondary">
                Replace <code>YOUR_TOKEN_HERE</code> with the value of{' '}
                <code>MCP_STATIC_TOKEN</code>.
                For production use replace <code>http://localhost:7007</code> with{' '}
                <code>https://backstage.cloud.wesecon.digital</code>.
              </Typography>
            </Box>

            {/* Claude Desktop */}
            <CodeBlock
              label="Claude Desktop — ~/.config/claude/claude_desktop_config.json"
              code={`{
  "mcpServers": {
    "backstage-catalog": {
      "url": "${BASE_URL}/api/mcp-actions/v1/catalog",
      "headers": {
        "Authorization": "Bearer YOUR_TOKEN_HERE"
      }
    }
  }
}`}
            />

            {/* Cursor */}
            <CodeBlock
              label="Cursor — .cursor/mcp.json (project root)"
              code={`{
  "mcpServers": {
    "backstage-catalog": {
      "url": "${BASE_URL}/api/mcp-actions/v1/catalog",
      "headers": {
        "Authorization": "Bearer YOUR_TOKEN_HERE"
      }
    }
  }
}`}
            />

            {/* VS Code Copilot */}
            <CodeBlock
              label="VS Code Copilot Chat — .vscode/mcp.json (workspace)"
              code={`{
  "servers": {
    "backstage-catalog": {
      "type": "http",
      "url": "${BASE_URL}/api/mcp-actions/v1/catalog",
      "headers": {
        "Authorization": "Bearer YOUR_TOKEN_HERE"
      }
    }
  }
}`}
            />
          </InfoCard>
        </Box>

        {/* ── Example AI prompts ─────────────────────────────────────────── */}
        <Box className={classes.section}>
          <InfoCard
            title="Example AI prompts"
            subheader="What to ask an MCP-connected AI client once configured"
          >
            {[
              {
                label: 'Catalog exploration',
                prompt:
                  'Use the list-entities-by-kind tool to list all API entities, then ' +
                  'summarize each one in a table showing name, owner, and description.',
              },
              {
                label: 'Documentation generation',
                prompt:
                  'Use summarize-entity-context for "api:default/my-service" and then ' +
                  'write a TechDocs-compatible README for that API, including an overview, ' +
                  'authentication details (from the annotations), and a getting-started section.',
              },
              {
                label: 'Incident runbook',
                prompt:
                  'Fetch the entity "component:default/my-service" and draft an incident ' +
                  'runbook covering common failure modes, escalation contacts (from the owner ' +
                  'field), and recovery steps based on the service type and lifecycle.',
              },
              {
                label: 'Catalog quality check',
                prompt:
                  'Use search-entities with kind="Component" and list every component that ' +
                  'has no description or no owner set. Group the results by namespace.',
              },
            ].map(({ label, prompt }) => (
              <Box key={label} mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {label}
                </Typography>
                <Paper elevation={0} className={classes.codeBlock}>
                  {prompt}
                </Paper>
              </Box>
            ))}
          </InfoCard>
        </Box>

        {/* ── Tags ──────────────────────────────────────────────────────── */}
        <Box display="flex" flexWrap="wrap">
          {['MCP', 'AI', 'Claude', 'Cursor', 'LLM', 'Backstage', 'catalog'].map(t => (
            <Chip key={t} label={t} size="small" className={classes.chip}/>
          ))}
        </Box>

      </Content>
    </Page>
  );
}
